<template>
  <div class="ads-list-page">
    <h2 class="ads-list-title">Most Clicks Ads</h2>

    <div v-if="isLoading" class="text-center py-2">
      <b-spinner label="Loading..." />
    </div>
    <b-alert v-else-if="loadError" show variant="danger" class="mb-1">
      {{ loadError }}
      <b-button size="sm" variant="outline-danger" class="ml-1" @click="fetchAds">Retry</b-button>
    </b-alert>
    <div v-else-if="!rows.length" class="text-center text-muted py-3">
      No ads with click data yet.
    </div>

    <b-card v-else no-body class="ads-table-card">
      <div class="ads-table-head">
        <div>No</div>
        <div>Ads</div>
        <div>Company</div>
        <div />
      </div>

      <div
        v-for="(item, index) in rows"
        :key="`most-${item.id}`"
        class="ads-table-row cursor-pointer"
        @click="openAdDetail(item)"
      >
        <div class="id-cell">{{ item.no }}</div>
        <div class="ads-cell">
          <b-img :src="item.adImage" class="ad-thumb" />
          <span class="ad-name">{{ item.adName }}</span>
        </div>
        <div class="company-cell">
          <b-avatar :src="item.companyLogo" size="34" class="company-avatar" />
          <span class="company-name">{{ item.companyName }}</span>
        </div>
        <div class="metric-cell">
          <span class="metric-text">{{ item.clicksLabel }}</span>
          <row-actions
            :show-edit="false"
            @delete="deleteRow(item, index)"
          />
        </div>
      </div>
    </b-card>

    <AdminPaginationBar
      :page="currentPage"
      :per-page="perPage"
      :total="totalRows"
      @change="onPageChange"
    />
  </div>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BButton,
  BCard,
  BImg,
  BSpinner,
} from 'bootstrap-vue'
import AdminPaginationBar from '@/components/admin/AdminPaginationBar.vue'
import RowActions from '@/components/admin/RowActions.vue'
import { adminPanelApi } from '@/services/adminPanelApi'
import { applyListPagination, resolveMediaUrl, unwrapAdminResponse } from '@/utils/adminApiHelpers'
import { confirmAction, notifyError, notifySuccess } from '@/utils/uiActions'

export default {
  name: 'AdsMostClick',
  components: {
    BAlert,
    BAvatar,
    BButton,
    BCard,
    BImg,
    BSpinner,
    AdminPaginationBar,
    RowActions,
  },
  data() {
    return {
      isLoading: false,
      loadError: '',
      rows: [],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
    }
  },
  created() {
    this.fetchAds()
  },
  methods: {
    onPageChange(page) {
      this.currentPage = page
      this.fetchAds()
    },
    mapRow(ad) {
      return {
        id: String(ad.id),
        no: String(ad.no).padStart(2, '0'),
        adImage: resolveMediaUrl(ad.ad?.image),
        adName: ad.ad?.title || 'Untitled ad',
        companyName: ad.company?.name || '—',
        companyLogo: resolveMediaUrl(ad.company?.logo),
        clicksLabel: ad.clicks || '0 Clicks',
      }
    },
    async fetchAds() {
      this.isLoading = true
      this.loadError = ''
      try {
        const res = await adminPanelApi.adsMostClicked({
          page: this.currentPage,
          per_page: this.perPage,
        })
        const payload = unwrapAdminResponse(res)
        const list = payload.most_clicked_ads || []
        applyListPagination(this, res, payload)
        this.rows = list.map(a => this.mapRow(a))
      } catch (e) {
        this.loadError = e.response?.data?.message || 'Unable to load most clicked ads.'
        this.rows = []
      } finally {
        this.isLoading = false
      }
    },
    openAdDetail(item) {
      this.$router.push({ name: 'ad-detail', params: { id: String(item.id) } })
    },
    deleteRow(item, index) {
      confirmAction(
        this,
        { title: 'Delete ad?', text: `Delete "${item.adName}"?`, confirmButtonText: 'Delete' },
        async () => {
          try {
            await adminPanelApi.deleteAd(item.id)
            this.rows = this.rows.filter((_, i) => i !== index)
            notifySuccess(this, 'Ad deleted successfully.')
          } catch (e) {
            notifyError(this, e.response?.data?.message || 'Unable to delete ad.')
          }
        },
      )
    },
  },
}
</script>

<style scoped lang="scss">
.ads-list-title {
  font-size: 2.6rem;
  font-weight: 800;
  margin: 0 0 1rem;
  color: #18181b;
}
.ads-table-card {
  border-radius: 10px;
  border: 1px solid #ecebf1;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}
.ads-table-head,
.ads-table-row {
  display: grid;
  grid-template-columns: 80px minmax(0, 1.8fr) minmax(0, 1fr) 180px;
  align-items: center;
}
.ads-table-head {
  padding: 0.7rem 1rem;
  background: #fbfbfd;
  border-bottom: 1px solid #ecebf1;
  color: #8d8fa1;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.ads-table-row { padding: 0.5rem 1rem; border-bottom: 1px solid #ecebf1; }
.ads-table-row:last-child { border-bottom: none; }
.ads-cell, .company-cell, .metric-cell { display: flex; align-items: center; min-width: 0; }
.ad-thumb { width: 44px; height: 34px; object-fit: cover; border-radius: 4px; margin-right: 0.75rem; }
.ad-name, .company-name, .id-cell { font-size: 1rem; color: #5f6274; }
.company-avatar { margin-right: 0.65rem; }
.metric-cell { justify-content: space-between; }
.metric-text { color: #5f6274; font-weight: 600; }
.icon-btn { padding: 0.3rem !important; color: #7e8294 !important; }

@media (max-width: 991.98px) {
  .ads-list-title { font-size: 2rem; }
  .ads-table-head, .ads-table-row {
    grid-template-columns: 70px minmax(0, 1.8fr) minmax(0, 1fr);
    row-gap: 0.55rem;
  }
  .metric-cell { grid-column: 1 / -1; justify-content: flex-start; gap: 1rem; }
}
</style>
